import { useEffect, useState } from "react";
import { Toast } from "./components/Toast";
import { modules } from "./config/modules";
import { AppLayout } from "./layout/AppLayout";
import { Agent } from "./pages/Agent";
import { Booking } from "./pages/Booking";
import { Dashboard } from "./pages/Dashboard";
import { GuestDetails } from "./pages/GuestDetails";
import { Login } from "./pages/Login";
import { LoginActivity } from "./pages/LoginActivity";
import { Property } from "./pages/Property";
import { Reports } from "./pages/Reports";
import { Rooms } from "./pages/Rooms";
import { SettingsPanel } from "./pages/SettingsPanel";
import { Staff } from "./pages/Staff";
import { Tenant } from "./pages/Tenant";

const SESSION_KEY = "letsbook.session";

function loadSession() {
  try {
    return JSON.parse(sessionStorage.getItem(SESSION_KEY)) || null;
  } catch {
    return null;
  }
}

function readLocation() {
  const raw = window.location.hash.replace(/^#\/?/, "");
  const [path, query] = raw.split("?");
  const params = new URLSearchParams(query || "");
  return {
    active: path || "dashboard",
    guestType: params.get("type") || "arrivals",
    guestDate: params.get("date") || "",
  };
}

function firstModule(list) {
  if (!list?.length) return "dashboard";
  return modules.find((m) => list.includes(m.id))?.id || list[0];
}

function labelFor(id) {
  return modules.find((m) => m.id === id)?.label || id;
}

export default function App() {
  const [session, setSession] = useState(loadSession);
  const [location, setLocation] = useState(readLocation);
  const [toast, setToast] = useState("");
  const [headerAction, setHeaderAction] = useState(null);

  const { active, guestType, guestDate } = location;

  useEffect(() => {
    const onHashChange = () => setLocation(readLocation());
    window.addEventListener("hashchange", onHashChange);
    return () => window.removeEventListener("hashchange", onHashChange);
  }, []);

  function goTo(hash, fallback) {
    setHeaderAction(null);
    if (window.location.hash === hash) {
      setLocation(fallback);
    } else {
      window.location.hash = hash;
    }
  }

  function navigate(id) {
    goTo(`#${id}`, { active: id, guestType, guestDate });
  }

  function openGuestDetails(type, date) {
    goTo(`#guestDetails?type=${encodeURIComponent(type)}&date=${encodeURIComponent(date)}`, {
      active: "guestDetails",
      guestType: type,
      guestDate: date,
    });
  }

  function notify(message) {
    setToast(message);
    setTimeout(() => setToast(""), 2600);
  }

  function handleLogin(data) {
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(data));
    setSession(data);
    navigate(firstModule(data?.allowedModules));
  }

  const currentTenantId = session?.tenantId || null;
  const agentUsername = session?.userType === "AGENT" ? session?.username || null : null;

  const allowedModules = session?.allowedModules || [];
  const unrestricted = !allowedModules.length;
  const hasAccess = (id) => {
    if (unrestricted) return true;
    if (id === "guestDetails") return allowedModules.includes("dashboard");
    return allowedModules.includes(id);
  };

  const defaultModule = firstModule(allowedModules);
  const effectiveActive = hasAccess(active) ? active : defaultModule;

  useEffect(() => {
    if (effectiveActive !== active) {
      notify(`You don't have access to "${active}" — showing ${labelFor(defaultModule)}.`);
      navigate(defaultModule);
    }
  }, [active, effectiveActive, defaultModule]);

  if (!session) {
    return <Login onLogin={handleLogin} notify={notify} toast={toast} />;
  }

  return (
    <AppLayout session={session} active={effectiveActive} setActive={navigate} onLogout={() => { sessionStorage.removeItem(SESSION_KEY); setSession(null); }} headerAction={headerAction}>
      {effectiveActive === "dashboard" && <Dashboard onTileClick={openGuestDetails} tenantId={(session?.role?.toLowerCase() === "super admin") ? null : currentTenantId} />}
      {effectiveActive === "guestDetails" && <GuestDetails type={guestType} date={guestDate} onBack={() => navigate("dashboard")} notify={notify} />}
      {effectiveActive === "property" && <Property notify={notify} setHeaderAction={setHeaderAction} tenantId={(session?.role?.toLowerCase() === "super admin") ? null : currentTenantId} />}
      {effectiveActive === "roomBooking" && <Booking notify={notify} tenantId={(session?.role?.toLowerCase() === "super admin") ? null : currentTenantId} agentUsername={agentUsername} userId={session?.userId} username={session?.username} />}
      {effectiveActive === "roomMaster" && <Rooms notify={notify} setHeaderAction={setHeaderAction} tenantId={(session?.role?.toLowerCase() === "super admin") ? null : currentTenantId} />}
      {effectiveActive === "agent" && <Agent notify={notify} setHeaderAction={setHeaderAction} tenantId={(session?.role?.toLowerCase() === "super admin") ? null : currentTenantId} />}
      {effectiveActive === "staff" && <Staff notify={notify} setHeaderAction={setHeaderAction} tenantId={(session?.role?.toLowerCase() === "super admin") ? null : currentTenantId} />}
      {effectiveActive === "tenant" && <Tenant notify={notify} setHeaderAction={setHeaderAction} tenantId={(session?.role?.toLowerCase() === "super admin") ? null : currentTenantId} isSuperAdmin={session?.role?.toLowerCase() === "super admin"} />}
      {effectiveActive === "reports" && <Reports notify={notify} tenantId={(session?.role?.toLowerCase() === "super admin") ? null : currentTenantId} />}
      {effectiveActive === "loginActivity" && <LoginActivity />}
      {effectiveActive === "settings" && <SettingsPanel notify={notify} userRole={session?.role || session?.userType} />}
      <Toast message={toast} />
    </AppLayout>
  );
}
